import { useMemo } from 'react';
import { StylesConfig } from 'react-select';
import { useThemeMode } from '../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

type CssProperty = 'backgroundColor' | 'color' | 'borderColor';

function resolveCssVar(property: CssProperty, varName: string, fallback: string): string {
  if (typeof document === 'undefined') {
    return fallback;
  }

  const probe = document.createElement('span');
  probe.style.display = 'none';
  probe.style[property] = `var(${varName})`;
  document.body.appendChild(probe);
  const resolved = getComputedStyle(probe)[property];
  document.body.removeChild(probe);

  if (!resolved || resolved === 'rgba(0, 0, 0, 0)' || resolved === 'transparent') {
    return fallback;
  }

  return resolved;
}

export const getReactSelectStyles = (): StylesConfig => {
  const root = document.documentElement;
  const isDark = root.getAttribute('data-bs-theme') === 'dark';

  const bgColor = resolveCssVar('backgroundColor', '--bs-body-bg', isDark ? '#1e1e2d' : '#ffffff');
  const textColor = resolveCssVar('color', '--bs-input-solid-color', isDark ? '#cdcdde' : '#5e6278');
  const inputBg = resolveCssVar('backgroundColor', '--bs-input-solid-bg', isDark ? '#1b1b29' : '#f5f8fa');
  const inputBgFocus = resolveCssVar('backgroundColor', '--bs-input-solid-bg-focus', isDark ? '#2b2b40' : '#eef3f7');
  const borderColor = resolveCssVar('borderColor', '--bs-border-color', isDark ? '#2b2b40' : '#e4e6ef');
  const placeholderColor = resolveCssVar('color', '--bs-input-solid-placeholder-color', isDark ? '#565674' : '#a1a5b7');
  const indicatorColor = resolveCssVar('color', '--bs-gray-500', isDark ? '#565674' : '#a1a5b7');

  return {
    control: (base, state) => ({
      ...base,
      backgroundColor: inputBg,
      borderColor: inputBg,
      color: textColor,
      minHeight: 'calc(1.5em + 1.1rem + 2px)',
      boxShadow: 'none',
      cursor: 'pointer',
      '&:hover': {
        borderColor: inputBg,
        backgroundColor: inputBg,
      },
      ...(state.isFocused
        ? {
            borderColor: inputBgFocus,
            backgroundColor: inputBgFocus,
            boxShadow: 'none',
          }
        : {}),
      ...(state.isDisabled
        ? {
            backgroundColor: inputBg,
            borderColor: inputBg,
            opacity: 0.65,
            cursor: 'not-allowed',
          }
        : {}),
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: bgColor,
      border: `1px solid ${borderColor}`,
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused || state.isSelected ? inputBgFocus : 'transparent',
      color: textColor,
      cursor: 'pointer',
      '&:active': {
        backgroundColor: inputBgFocus,
      },
    }),
    singleValue: (base) => ({
      ...base,
      color: textColor,
    }),
    input: (base) => ({
      ...base,
      color: textColor,
    }),
    placeholder: (base) => ({
      ...base,
      color: placeholderColor,
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: borderColor,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: indicatorColor,
      '&:hover': {
        color: textColor,
      },
    }),
    clearIndicator: (base) => ({
      ...base,
      color: indicatorColor,
      '&:hover': {
        color: textColor,
      },
    }),
    loadingIndicator: (base) => ({
      ...base,
      color: indicatorColor,
    }),
    noOptionsMessage: (base) => ({
      ...base,
      color: textColor,
    }),
    loadingMessage: (base) => ({
      ...base,
      color: textColor,
    }),
  };
};

export function useReactSelectStyles() {
  const { mode } = useThemeMode();
  return useMemo(() => getReactSelectStyles(), [mode]);
}
